import { Button } from "@msuite/ui/button";
import { Divider } from "@msuite/ui/divider";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { formatNumber } from "@msuite/utils/numbers";
import type { CalculationFormSchema } from "@msuite/utils/schemas";
import { calculateService } from "@msuite/utils/services";
import type { ServiceUnit } from "@msuite/utils/types";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { CalculationFormItemPosition } from "./calculation-form-item-position";

/** Props Interface */
interface CalculationFormItemPositionsProps {
	index: number;
	unit: ServiceUnit;
}

export function CalculationFormItemPositions({ index, unit }: CalculationFormItemPositionsProps) {
	/** Context */
	const methods = useFormContext<CalculationFormSchema>();
	const item = useWatch({ control: methods.control, name: `services.${index}` });

	/** Hooks */
	const { t } = useTranslation();
	const { fields, append, remove } = useFieldArray({
		control: methods.control,
		name: `services.${index}.positions`,
	});

	/** Computed */
	const total = calculateService(item);

	/** Functions */
	function handleOnAddPosition() {
		const previous = item.positions?.at(-1);
		append({
			...previous,
			id: undefined,
			name: "",
			ranking: fields.length,
		});
	}

	/** Render */
	return (
		<div className="v-stack">
			{fields.length > 0 && <Divider variant="content-card" />}
			{fields.map((field, positionIndex) => (
				<CalculationFormItemPosition
					key={field.id}
					index={index}
					positionIndex={positionIndex}
					unit={unit}
					unitPrice={item.unitPrice}
					onRemove={() => remove(positionIndex)}
				/>
			))}
			<div className="h-stack justify-between items-center">
				<Button
					icon={<Icon name="Plus" />}
					onClick={handleOnAddPosition}
				>
					{t("calculations.add_position")}
				</Button>
				{fields.length > 0 && (
					<div className="h-stack items-center">
						<p className="label text-base">{t("invoices.sum_net")}</p>
						<p>{formatNumber(total, "currency")}</p>
					</div>
				)}
			</div>
		</div>
	);
}
